import React from "react";
import { Shield, CheckCircle2, Euro, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import type { NursingHomeFilters } from "@/hooks/use-county-page-data";

interface SmartFiltersProps {
  filters: NursingHomeFilters;
  setFilters: (filters: NursingHomeFilters) => void;
  activeFilterCount: number;
}

export function SmartFilters({ filters, setFilters, activeFilterCount }: SmartFiltersProps) {
  const clearAll = () => {
    setFilters({ inspectionRating: null, fairDealOnly: false, priceRange: null });
  };

  return (
    <div className="flex flex-wrap items-center gap-3 mt-4">
      {/* Inspection Rating */}
      <div className="flex items-center gap-1.5">
        <Shield size={14} className="text-muted-foreground shrink-0" />
        <Select
          value={filters.inspectionRating || "all"}
          onValueChange={(v) =>
            setFilters({ ...filters, inspectionRating: v === "all" ? null : v })
          }
        >
          <SelectTrigger className="h-9 w-[180px] text-sm bg-background">
            <SelectValue placeholder="HIQA Rating" />
          </SelectTrigger>
          <SelectContent className="bg-background z-50">
            <SelectItem value="all">Any Rating</SelectItem>
            <SelectItem value="compliant">Compliant</SelectItem>
            <SelectItem value="substantially_compliant">Substantially Compliant</SelectItem>
            <SelectItem value="non_compliant">Non-Compliant</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Fair Deal Toggle */}
      <div className="flex items-center gap-2 rounded-md border px-3 h-9 bg-background">
        <CheckCircle2 size={14} className="text-blue-600 shrink-0" />
        <Label htmlFor="fair-deal-filter" className="text-sm cursor-pointer whitespace-nowrap">
          Fair Deal
        </Label>
        <Switch
          id="fair-deal-filter"
          checked={filters.fairDealOnly}
          onCheckedChange={(checked) =>
            setFilters({ ...filters, fairDealOnly: checked })
          }
          className="scale-75"
        />
      </div>

      {/* Price Range */}
      <div className="flex items-center gap-1.5">
        <Euro size={14} className="text-muted-foreground shrink-0" />
        <Select
          value={filters.priceRange || "all"}
          onValueChange={(v) =>
            setFilters({ ...filters, priceRange: v === "all" ? null : v })
          }
        >
          <SelectTrigger className="h-9 w-[180px] text-sm bg-background">
            <SelectValue placeholder="Price Range" />
          </SelectTrigger>
          <SelectContent className="bg-background z-50">
            <SelectItem value="all">Any Price</SelectItem>
            <SelectItem value="under_1000">Under €1,000/week</SelectItem>
            <SelectItem value="1000_1300">€1,000 – €1,300/week</SelectItem>
            <SelectItem value="1300_1600">€1,300 – €1,600/week</SelectItem>
            <SelectItem value="over_1600">Over €1,600/week</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Clear Filters */}
      {activeFilterCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearAll}
          className="h-9 text-sm text-muted-foreground hover:text-foreground gap-1"
        >
          <X size={14} />
          Clear {activeFilterCount > 1 ? `(${activeFilterCount})` : ""}
        </Button>
      )}
    </div>
  );
}
